/**
 * The failures preview card (platform design §6.5): the most recent failures, each with its
 * reason sentence, the same one the failures list shows.
 */

import { Link } from '@tanstack/react-router';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { EmptyState, QueryStates } from '../../../chrome/state';
import { getLocale } from '../../../i18n';
import type { ApiFailureError } from '../../../lib/http/errors';
import { Card, CardContent, CardHeader } from '../../../ui/card';
import type { Failure } from './api/schemas';
import { formatTime } from './dayHeading';
import { REASON_ICON, reasonSentence } from './failureReasons';
import { cm } from './messages';
import { FAILURES_PATH } from './paths';

const PREVIEW_ROWS = 3;

export function FailuresPreviewCard(props: {
  campaignId: string;
  query: UseQueryResult<Failure[], ApiFailureError>;
}): ReactNode {
  const locale = getLocale();
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <h2 className="text-title-sm">{cm('failures.title')}</h2>
        <Link
          to={FAILURES_PATH}
          params={{ campaignId: props.campaignId }}
          className="text-caption text-primary-ink hover:underline"
        >
          {cm('failures.viewAll')}
        </Link>
      </CardHeader>
      <CardContent>
        <QueryStates
          query={props.query}
          empty={<EmptyState title={cm('failures.empty.title')} tone="neutral" />}
        >
          {(failures) => (
            <ul className="flex flex-col divide-y" data-testid="failures-list">
              {failures.slice(0, PREVIEW_ROWS).map((failure) => {
                const Icon = REASON_ICON[failure.reason];
                return (
                  <li
                    key={`${failure.target_id}-${failure.scheduled_slot}`}
                    className="flex items-start gap-2 py-2"
                  >
                    <Icon
                      aria-hidden
                      data-testid={`failure-reason-icon-${failure.reason}`}
                      className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                    />
                    <div>
                      <p className="text-body-sm font-medium">
                        {cm('failures.page.rowTitle', {
                          account:
                            failure.account_label ?? failure.account_handle ?? failure.target_id,
                          time: formatTime(new Date(failure.scheduled_slot), locale),
                        })}
                      </p>
                      <p className="text-caption text-muted-foreground">
                        {reasonSentence(failure.reason)}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </QueryStates>
      </CardContent>
    </Card>
  );
}
